import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TarotCard } from './TarotCard';
import { CelticCrossLayout } from './CelticCrossLayout';
import { SpreadInterpretation } from './SpreadInterpretation';
import { ExportMenu } from './ExportMenu';
import { DeepDiveModal } from './DeepDiveModal';
import { Button, Card, Modal, Loading, toast } from '../common';
import TagInput from '../history/TagInput';
import type { DrawnCard, TarotSpread } from '../../types/tarot';
import { generateBasicInterpretation, getCardMeaning } from '../../utils/tarotLogic';
import { readingsAPI } from '../../api/readings';
import { useEquippedDecorations } from '../../hooks/useDecorations';
import { useInventory } from '../../hooks/useShop';
import { getResponsiveBackgroundUrl } from '../../utils/decorationAssets';
import { useCreateDeepDive } from '../../hooks/useReadings';
import { Layers } from 'lucide-react';

// Helper to convert deck name to deck ID
function getDeckIdFromName(deckName: string | null): string | null {
  if (!deckName) return null;
  const nameLower = deckName.toLowerCase();
  if (nameLower.includes('celestial wisdom') || nameLower.includes('celestial-wisdom')) {
    return 'celestial-wisdom';
  }
  if (nameLower.includes('mystic dreams') || nameLower.includes('mystic-dreams')) {
    return 'mystic-dreams';
  }
  if (nameLower.includes('dark gothic') || nameLower.includes('dark-gothic')) {
    return 'dark-gothic';
  }
  if (nameLower.includes('rider-waite') || nameLower.includes('rider waite') || nameLower.includes('classic')) {
    return 'rider-waite';
  }
  return null;
}

// Helper to get background ID from background name
function getBackgroundIdFromName(backgroundName: string | null): string | null {
  if (!backgroundName) return null;
  const nameLower = backgroundName.toLowerCase();
  if (nameLower.includes('starry night')) return 'starry-night';
  if (nameLower.includes('aurora borealis')) return 'aurora-borealis';
  if (nameLower.includes('mystic forest')) return 'mystic-forest';
  return null;
}

interface ReadingResultProps {
  cards: DrawnCard[];
  spread: TarotSpread;
  question: string;
  aiInterpretation?: string;
  onSaveReading?: (interpretation: string, tags?: string[]) => void;
  onNewReading: () => void;
  readingId?: string;
  /** If true, the reading is completed and deep dive is available */
  isCompleted?: boolean;
}

export function ReadingResult({ 
  cards, 
  spread, 
  question,
  aiInterpretation: initialAiInterpretation,
  onSaveReading, 
  onNewReading,
  readingId,
  isCompleted = true,
}: ReadingResultProps) {
  const [selectedCard, setSelectedCard] = useState<DrawnCard | null>(null);
  const [showFullInterpretation, setShowFullInterpretation] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [aiInterpretation, setAiInterpretation] = useState<string | undefined>(initialAiInterpretation);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [showDeepDive, setShowDeepDive] = useState(false);

  const navigate = useNavigate();
  const createDeepDive = useCreateDeepDive();
  const { refetch: refetchInventory } = useInventory();
  
  // Fetch equipped decorations
  const { data: equippedDecorations } = useEquippedDecorations();
  
  // Extract decoration settings
  const equippedDeckId = getDeckIdFromName(equippedDecorations?.cardImageDeck || null);
  const equippedCardBackPath = equippedDecorations?.cardBack?.filePath || null;
  const equippedBackgroundName = equippedDecorations?.background?.name || null;
  const backgroundId = getBackgroundIdFromName(equippedBackgroundName);
  
  // Get background URL if equipped
  const backgroundUrl = backgroundId ? getResponsiveBackgroundUrl(backgroundId, false) : null;
  
  const basicInterpretation = generateBasicInterpretation(cards, spread, question);
  
  // Debug: Log cards to verify imagePath
  useEffect(() => {
    console.log('ReadingResult - Cards received:', cards);
    console.log('ReadingResult - Cards count:', cards.length);
    console.log('ReadingResult - Sample card:', cards[0]);
    console.log('ReadingResult - All cards have imagePath:', cards.every(c => c.imagePath));
  }, [cards]);
  
  // Fetch reading data if readingId is provided
  useEffect(() => {
    if (readingId && !aiInterpretation) {
      setIsLoadingAI(true);
      readingsAPI.getReading(readingId)
        .then(response => {
          // The API returns: { success: true, data: { reading: { ... } } }
          const reading = (response.data as any).reading || response.data;
          if (reading.aiInterpretation) {
            setAiInterpretation(reading.aiInterpretation);
          }
          if (reading.tags) {
            setTags(reading.tags);
          }
        })
        .catch(error => {
          console.error('Failed to fetch reading:', error);
        })
        .finally(() => {
          setIsLoadingAI(false);
        });
    }
  }, [readingId, aiInterpretation]);
  
  const handleSave = () => {
    onSaveReading?.(basicInterpretation, tags);
  };

  const handleDeepDiveSubmit = async (deepQuestion: string, cardCount: number) => {
    if (!readingId) return;
    try {
      const response = await createDeepDive.mutateAsync({
        parentReadingId: readingId,
        question: deepQuestion,
        cardCount,
      });
      const deepReadingId = (response.data as any)?.reading?.id || (response.data as any)?.id;
      toast.success('Deep dive started! Generating interpretation...');
      setShowDeepDive(false);
      if (deepReadingId) {
        navigate(`/reading/${deepReadingId}`);
      }
    } catch {
      // error handled by hook
    }
  };
  
  return (
    <div id="reading-content" className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">
          Your Reading
        </h2>
        <p className="text-gray-400 mb-4">
          "{question}"
        </p>
        <div className="text-sm text-primary-500">
          {spread.name} • {cards.length} cards
        </div>
      </div>

      {/* Cards Display */}
      <Card 
        className="relative bg-dark-800"
        style={backgroundUrl ? {
          backgroundImage: `url(${backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : undefined}
      >
        {/* Background overlay for readability */}
        {backgroundUrl && (
          <div className="absolute inset-0 bg-black/40 rounded-lg" />
        )}
        
        <div className="relative h-full p-4 md:p-8">
          {spread.id === 'celtic-cross' ? (
            <CelticCrossLayout
              cards={cards}
              spread={spread}
              revealedCards={new Set(cards.map((_, i) => i))}
              showMeanings={true}
              onCardClick={(index) => setSelectedCard(cards[index])}
              equippedDeckId={equippedDeckId}
              equippedCardBackPath={equippedCardBackPath}
            />
          ) : cards.length === 1 ? (
            // Single card: center it without absolute positioning
            <div className="flex justify-center items-center min-h-96">
              <div className="text-center">
                <TarotCard
                  card={cards[0]}
                  isRevealed={true}
                  size="lg"
                  showMeaning={true}
                  enableHoverEffects={true}
                  equippedDeckId={equippedDeckId}
                  equippedCardBackPath={equippedCardBackPath}
                  onClick={() => setSelectedCard(cards[0])}
                />
                <div className="mt-4 space-y-2">
                  <div className="text-sm font-semibold text-white">
                    {spread.positions[0].name}
                  </div>
                  <div className="text-xs text-gray-400">
                    Click for details
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Multiple cards: use absolute positioning
            cards.map((card, index) => {
              const position = spread.positions[index];
              
              return (
                <div
                  key={`${card.id}-${index}`}
                  className="absolute cursor-pointer"
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  onClick={() => setSelectedCard(card)}
                >
                  <div className="text-center">
                    <TarotCard
                      card={card}
                      isRevealed={true}
                      size="md"
                      showMeaning={true}
                      enableHoverEffects={true}
                      position={index + 1}
                      spreadType={spread.id}
                      equippedDeckId={equippedDeckId}
                      equippedCardBackPath={equippedCardBackPath}
                    />
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-semibold text-white">
                        {position.name}
                      </div>
                      <div className="text-xs text-gray-400">
                        Click for details
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Interpretation Section */}
      {isLoadingAI ? (
        <Card className="text-center py-12">
          <Loading size="lg" />
          <p className="text-gray-400 mt-4">Loading AI interpretation...</p>
        </Card>
      ) : (
        <SpreadInterpretation
          cards={cards}
          spread={spread}
          question={question}
          aiInterpretation={aiInterpretation}
          basicInterpretation={basicInterpretation}
        />
      )}
      
      {/* Tags Section */}
      {onSaveReading && (
        <Card className="max-w-2xl mx-auto">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Add Tags</h3>
            <p className="text-sm text-gray-400">
              Tag this reading to help organize and find it later
            </p>
            <TagInput
              value={tags}
              onChange={setTags}
              placeholder="Add tags like 'love', 'career', 'guidance'..."
              readingText={`${question} ${basicInterpretation}`}
              maxTags={8}
            />
          </div>
        </Card>
      )}
      
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          variant="secondary"
          onClick={onNewReading}
          className="flex-1 sm:flex-none"
        >
          New Reading
        </Button>
        
        {onSaveReading && (
          <Button
            onClick={handleSave}
            className="flex-1 sm:flex-none"
          >
            Save Reading
          </Button>
        )}
        
        {readingId && isCompleted && (
          <Button
            variant="secondary"
            onClick={() => setShowDeepDive(true)}
            className="flex-1 sm:flex-none flex items-center gap-2"
          >
            <Layers className="h-4 w-4" />
            Deep Dive
          </Button>
        )}
        
        <ExportMenu
          reading={{
            question,
            cards,
            spread,
            interpretation: basicInterpretation,
            aiInterpretation
          }}
          readingId={readingId}
          elementId="reading-content"
        />
      </div>
      
      {/* Card Detail Modal */}
      {selectedCard && (
        <Modal
          isOpen={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          title={selectedCard.name}
          size="md"
        >
          <div className="space-y-4">
            <div className="flex justify-center">
              <TarotCard
                card={selectedCard}
                isRevealed={true}
                size="lg"
                equippedDeckId={equippedDeckId}
                equippedCardBackPath={equippedCardBackPath}
              />
            </div>
            
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-white mb-1">Position</h4>
                <p className="text-gray-400 text-sm">
                  {selectedCard.positionMeaning}
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-1">
                  {selectedCard.isReversed ? 'Reversed Meaning' : 'Upright Meaning'}
                </h4>
                <div className="text-gray-300 text-sm">
                  {getCardMeaning(selectedCard).map((meaning, index) => (
                    <div key={index} className="mb-1">• {meaning}</div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-1">Description</h4>
                <p className="text-gray-400 text-sm">
                  {selectedCard.description}
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-1">Keywords</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedCard.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="text-xs bg-primary-500/20 text-primary-400 px-2 py-1 rounded"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Deep Dive Modal */}
      {readingId && (
        <DeepDiveModal
          isOpen={showDeepDive}
          onClose={() => setShowDeepDive(false)}
          onSubmit={handleDeepDiveSubmit}
          isSubmitting={createDeepDive.isPending}
          onSuccess={() => refetchInventory()}
        />
      )}

      {/* Full Interpretation Modal */}
      <Modal
        isOpen={showFullInterpretation}
        onClose={() => setShowFullInterpretation(false)}
        title="Full Reading Interpretation"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-dark-700 p-4 rounded">
            <h4 className="font-semibold text-white mb-2">Your Question</h4>
            <p className="text-gray-300 italic">"{question}"</p>
          </div>
          
          <div className="whitespace-pre-line text-gray-300 text-sm leading-relaxed">
            {basicInterpretation}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button
              variant="secondary"
              onClick={() => setShowFullInterpretation(false)}
            >
              Close
            </Button>
            {onSaveReading && (
              <Button onClick={handleSave}>
                Save This Reading
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}